<template>
  <div class="tx-quick-board" :class="{ 'betting-closed': !isBetOpen }">
    <div class="tx-quick-board__main">
      <button
        type="button"
        class="tx-quick-board__side tx-quick-board__side--tai"
        :class="cellClass('tai')"
        :disabled="!isBetOpen || isSelectionBlocked('tai')"
        @click="onBet('tai')"
      >
        <span class="tx-quick-board__title">TÀI</span>
        <span class="tx-quick-board__sub">BIG<i />11-17</span>
        <BetPlacedChip v-if="displayBets.tai" :amount="displayBets.tai" :chips="chips" placement="top-left" />
      </button>

      <div class="tx-quick-board__odd-even">
        <button
          v-for="side in oddEven"
          :key="side.key"
          type="button"
          class="tx-quick-board__parity"
          :class="cellClass(side.key)"
          :disabled="!isBetOpen || isSelectionBlocked(side.key)"
          @click="onBet(side.key)"
        >
          <span class="tx-quick-board__parity-title">{{ side.title }}</span>
          <span class="tx-quick-board__parity-sub">{{ side.sub }}</span>
          <BetPlacedChip
            v-if="displayBets[side.key]"
            :amount="displayBets[side.key]"
            :chips="chips"
            placement="top-right"
            overlap
          />
        </button>
      </div>

      <button
        type="button"
        class="tx-quick-board__side tx-quick-board__side--xiu"
        :class="cellClass('xiu')"
        :disabled="!isBetOpen || isSelectionBlocked('xiu')"
        @click="onBet('xiu')"
      >
        <span class="tx-quick-board__title">XỈU</span>
        <span class="tx-quick-board__sub">SMALL<i />4-10</span>
        <BetPlacedChip v-if="displayBets.xiu" :amount="displayBets.xiu" :chips="chips" placement="top-right" />
      </button>
    </div>

    <div class="tx-quick-board__sums">
      <button
        v-for="sum in sums"
        :key="sum"
        type="button"
        class="tx-quick-board__sum"
        :class="cellClass(`sum-${sum}`)"
        :disabled="!isBetOpen"
        :title="totalOdds(sum)"
        @click="onBet(`sum-${sum}`)"
      >
        {{ sum }}
        <BetPlacedChip
          v-if="displayBets[`sum-${sum}`]"
          class="tx-quick-board__mini-chip"
          :amount="displayBets[`sum-${sum}`]"
          :chips="chips"
        />
      </button>
    </div>

    <div class="tx-quick-board__singles">
      <button
        v-for="face in dieFaces"
        :key="face"
        type="button"
        class="tx-quick-board__single"
        :class="cellClass(`single-${face}`)"
        :disabled="!isBetOpen"
        @click="onBet(`single-${face}`)"
      >
        <img :src="diceImage(face)" :alt="`Single ${face}`" />
        <BetPlacedChip
          v-if="displayBets[`single-${face}`]"
          class="tx-quick-board__mini-chip"
          :amount="displayBets[`single-${face}`]"
          :chips="chips"
        />
      </button>
    </div>

    <div class="tx-quick-board__footer">
      <!-- Tap: take back the last chip. Hold: clear them all (as the full board). -->
      <button
        type="button"
        class="tx-quick-board__undo"
        :aria-label="$t('Undo')"
        :disabled="!canUndo"
        @mousedown="onUndoPressStart"
        @mouseup="onUndoPressEnd"
        @mouseleave="onUndoPressCancel"
      >
        <img src="@/assets/svg/btns/undo.svg" alt="" />
        <small>{{ $t('Undo') }}</small>
      </button>

      <button
        type="button"
        class="tx-quick-board__arrow"
        :aria-label="$t('Previous chip')"
        :disabled="cycleChips.length < 2"
        @click="stepChip(-1)"
      >
        <img src="@/assets/svg/tx-quickplay/chevron.svg" alt="" />
      </button>

      <span class="tx-quick-board__chip">
        <img :src="activeChipImage" alt="" />
        <span :class="chipValueClass">{{ activeChipLabel }}</span>
      </span>

      <button
        type="button"
        class="tx-quick-board__arrow tx-quick-board__arrow--next"
        :aria-label="$t('Next chip')"
        :disabled="cycleChips.length < 2"
        @click="stepChip(1)"
      >
        <img src="@/assets/svg/tx-quickplay/chevron.svg" alt="" />
      </button>

      <button
        type="button"
        class="tx-quick-board__submit"
        :disabled="isSubmitting || isRolling || !isBetOpen || !hasBets"
        @click="$emit('submit')"
      >
        {{ isRolling ? $t('Rolling') : $t('Bet') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BetPlacedChip from '@/components/tai-xiu/bet-placed-chip.vue';
import { EXCLUSIVE_SELECTIONS, getTaiXiuDice } from '@/store/modules/tai-xiu';
import { TAI_XIU_BET_OPEN_STATUSES, TAI_XIU_RESULT_STATUSES } from '@/utils/tai-xiu.model';
import { TaiXiuBetType } from '@/utils/index.model';
import { fallbackTotalGameOdds, formatTaiXiuOdds, taiXiuGameOdds } from '@/utils/tai-xiu-market';

const CHIP_COLORS = ['blue', 'orange', 'black', 'purple', 'yellow'];

/*
 * The desktop quick-play bet board (Figma `quickplay`, node 457:7038).
 *
 * Deliberately not `tai-xiu/bet-board.vue` in compact mode: that board is laid
 * out for a phone-width sheet and is stable on mobile, and squeezing it into
 * the 264px rail took a wall of `::v-deep` overrides that still did not match
 * the design. This is its own markup; the bet keys (`tai`, `sum-4`, `single-1`
 * ...), the events and the open/blocked/result rules are the same as the mobile
 * board's, so both drive the `taiXiu` store identically.
 *
 * The chip is picked with the < > arrows, which cycle through the configured
 * chips. Undo was added after the design (PO request): tap takes back the last
 * chip, hold clears the slip, as on the full board (bet-board-actions.vue).
 */
const UNDO_HOLD_MS = 500;

export default {
  name: 'TxQuickBoard',
  components: {
    BetPlacedChip,
  },
  props: {
    table: {
      type: Object,
      required: true,
    },
    activeChip: {
      type: Number,
      required: true,
    },
    draftBets: {
      type: Object,
      required: true,
    },
    placedBets: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isRolling: {
      type: Boolean,
      default: false,
    },
    canUndo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      undoHoldTimer: null,
      sums: [4, 5, 6, 7, 8, 9, 10, 17, 16, 15, 14, 13, 12, 11],
      dieFaces: [1, 2, 3, 4, 5, 6],
      oddEven: [
        { key: 'odd', title: 'LẺ', sub: 'ODD' },
        { key: 'even', title: 'CHẴN', sub: 'EVEN' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      config: 'general/config',
      arenaBetChips: 'general/arenaBetChips',
      arenaCustomBetChip: 'general/arenaCustomBetChip',
    }),
    chips() {
      // Same source as the mobile board: the first four chips of this table's
      // arena, falling back to the global config.
      const chips = this.arenaBetChips(this.table && this.table.arenaId);
      return chips.length ? chips.slice(0, 4) : [5, 25, 100, 500];
    },
    /*
     * The arrows cycle the four arena chips plus the member's custom chip --
     * the fifth slot of the full board, saved per arena -- once one is set.
     * It lands on index 4, so it takes the custom chip's yellow art.
     */
    cycleChips() {
      const custom = Number(this.arenaCustomBetChip(this.table && this.table.arenaId)) || 0;
      return custom > 0 && !this.chips.includes(custom) ? [...this.chips, custom] : this.chips;
    },
    activeChipIndex() {
      return Math.max(0, this.cycleChips.indexOf(Number(this.activeChip)));
    },
    activeChipImage() {
      return require(`@/assets/svg/chips/${CHIP_COLORS[this.activeChipIndex] || 'blue'}.svg`);
    },
    activeChipLabel() {
      const value = Number(this.activeChip);
      if (value >= 1e6) return `${parseFloat((value / 1e6).toFixed(1))}M`;
      if (value >= 1e4) return `${parseFloat((value / 1e3).toFixed(1))}K`;
      return String(value);
    },
    chipValueClass() {
      return this.activeChipLabel.length >= 4 ? 'small' : '';
    },
    displayBets() {
      return Object.keys(this.draftBets).reduce(
        (bets, selection) => ({
          ...bets,
          [selection]: (bets[selection] || 0) + this.draftBets[selection],
        }),
        { ...this.placedBets }
      );
    },
    hasBets() {
      return Object.keys(this.draftBets).some((key) => this.draftBets[key] > 0);
    },
    isBetOpen() {
      return TAI_XIU_BET_OPEN_STATUSES.includes(this.table.fightStatus);
    },
    tableResultDice() {
      return getTaiXiuDice(this.table.result) || null;
    },
    resultTotal() {
      return this.tableResultDice ? this.tableResultDice.reduce((total, face) => total + face, 0) : null;
    },
    showResultHighlights() {
      return (
        !!this.tableResultDice &&
        TAI_XIU_RESULT_STATUSES.includes(this.table.fightStatus)
      );
    },
  },
  watch: {
    // A custom chip is a valid pick too; resetting on it threw the member back
    // to the first chip whenever the arena's chips were re-fetched.
    cycleChips: {
      immediate: true,
      handler(chips) {
        if (chips.length && !chips.includes(Number(this.activeChip))) {
          this.$emit('select-chip', chips[0]);
        }
      },
    },
  },
  beforeDestroy() {
    this.onUndoPressCancel();
  },
  methods: {
    onUndoPressStart() {
      if (!this.canUndo) return;
      this.onUndoPressCancel();
      this.undoHoldTimer = window.setTimeout(() => {
        this.undoHoldTimer = null;
        this.$emit('clear');
      }, UNDO_HOLD_MS);
    },
    onUndoPressEnd() {
      if (this.undoHoldTimer) {
        this.onUndoPressCancel();
        if (this.canUndo) this.$emit('undo');
      }
    },
    onUndoPressCancel() {
      if (this.undoHoldTimer) clearTimeout(this.undoHoldTimer);
      this.undoHoldTimer = null;
    },
    cellClass(selection) {
      return {
        selected: !!this.displayBets[selection],
        'result-matched': this.isResultMatch(selection),
        blocked: this.isSelectionBlocked(selection),
      };
    },
    onBet(selection) {
      if (!this.isBetOpen) return;
      this.$emit('bet', selection);
    },
    stepChip(direction) {
      const count = this.cycleChips.length;
      if (!count) return;
      this.$emit('select-chip', this.cycleChips[(this.activeChipIndex + direction + count) % count]);
    },
    diceImage(face) {
      return require(`@/assets/svg/dice-items/dice-${face}.svg`);
    },
    totalOdds(sum) {
      const value = taiXiuGameOdds(this.table.odds && this.table.odds[TaiXiuBetType.total4 + sum - 4]);
      return formatTaiXiuOdds(value === null ? fallbackTotalGameOdds(sum) : value);
    },
    isSelectionBlocked(selection) {
      // Tài/Xỉu and Lẻ/Chẵn are exclusive once one side is placed.
      const opposite = EXCLUSIVE_SELECTIONS[selection];
      return !!opposite && !!this.placedBets[opposite];
    },
    isResultMatch(selection) {
      if (!this.showResultHighlights) return false;
      const total = this.resultTotal;
      if (selection === 'tai') return total >= 11 && total <= 17;
      if (selection === 'xiu') return total >= 4 && total <= 10;
      if (selection === 'odd') return total % 2 === 1;
      if (selection === 'even') return total % 2 === 0;

      const [market, rawFace] = selection.split('-');
      const face = Number(rawFace);
      if (market === 'sum') return total === face;
      if (market === 'single') return this.tableResultDice.includes(face);
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/mixins/_tx-result-border.scss';

/* The v2.1 prototype's small Tài Xỉu board (TxBoard size "sm"), in a 268px column. */
.tx-quick-board {
  display: flex;
  flex-direction: column;
  color: #fff;

  button {
    position: relative;
    margin: 0;
    padding: 0;
    border: 0;
    font-family: inherit;
    color: inherit;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }

  .result-matched {
    @include tx-result-matched-border;
  }
}

/* Big | Lẻ Chẵn | Small -- 1.6 / .8 + .8 / 1.6, 68 tall. */
.tx-quick-board__main {
  height: 56px;
  display: grid;
  grid-template-columns: 76px 1fr 76px;
}

.tx-quick-board__side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  border: 1px solid;
  box-sizing: border-box;

  &--tai {
    border-color: transparent;
    border-radius: 8px 0 0 0;
    background: linear-gradient(160deg, #c33a2f, #a01f1f 55%, #6b1414);

    .tx-quick-board__sub {
      color: rgba(255, 255, 255, 0.82);
    }
  }

  &--xiu {
    border-color: transparent;
    border-radius: 0 8px 0 0;
    background: linear-gradient(200deg, #3a86dc, #1f62b8 50%, #0d3f7a);

    .tx-quick-board__sub {
      color: rgba(255, 255, 255, 0.82);
    }
  }

  // Placed: the zone keeps its colour and takes the gold ring and the lift.
  &.selected {
    box-shadow: inset 0 0 0 2px #e6cf87, inset 0 0 0 999px rgba(255, 255, 255, 0.08);
  }
}

.tx-quick-board__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-transform: uppercase;
}

.tx-quick-board__sub {
  display: flex;
  align-items: center;
  font-size: 9px;
  font-weight: 700;
  line-height: 12px;
  white-space: nowrap;

  i {
    width: 2px;
    height: 2px;
    margin: 0 3px;
    border-radius: 50%;
    background: currentColor;
  }
}

.tx-quick-board__odd-even {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid transparent;
  border-width: 1px 0;
  background: linear-gradient(180deg, #24a35c, #157140);
}

.tx-quick-board__parity {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: transparent;

  & + & {
    border-left: 1px solid rgba(255, 255, 255, 0.12);
  }

  &.selected {
    box-shadow: inset 0 0 0 2px #e6cf87, inset 0 0 0 999px rgba(255, 255, 255, 0.08);
  }
}

.tx-quick-board__parity-title {
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;
}

.tx-quick-board__parity-sub {
  color: rgba(255, 255, 255, 0.82);
  font-size: 9px;
  font-weight: 700;
  line-height: 12px;
}

/* Two rows of seven sums, 1px #1E2E4E grid lines, 63px tall. */
.tx-quick-board__sums {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 30px;
  gap: 1px;
  padding: 1px;
  margin-top: -1px;
  background: #1e2e4e;
}

.tx-quick-board__sum {
  background: $ga-tx-cell-bg;
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;

  &.selected {
    box-shadow: inset 0 0 0 2px #e6cf87, inset 0 0 0 999px rgba(255, 255, 255, 0.08);
  }
}

/* Single-die strip: 6 x 44px, #161E1F, rounded at the bottom. */
.tx-quick-board__singles {
  height: 32px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  border: 1px solid #1e2e4e;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: $ga-tx-cell-bg;
  overflow: hidden;
  box-sizing: border-box;
}

.tx-quick-board__single {
  display: grid;
  place-items: center;
  background: transparent;

  & + & {
    border-left: 1px solid #1e2e4e;
  }

  img {
    width: 18px;
    height: 18px;
    display: block;
  }

  &.selected {
    box-shadow: inset 0 0 0 2px #e6cf87, inset 0 0 0 999px rgba(255, 255, 255, 0.08);
  }
}

/* The placed-bet marker on a small cell: a 12px chip, top-right, no amount. */
.tx-quick-board__mini-chip.bet-placed-chip {
  top: 3px;
  right: 3px;
  left: auto;
  width: 12px;
  height: 12px;
  transform: none;

  ::v-deep .bet-placed-chip__value {
    display: none;
  }
}

/* UNDO < chip > ........ BET */
.tx-quick-board__footer {
  height: 40px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/*
 * The circle is centred on the row, like the arrows and the chip; the label
 * hangs over its lower edge instead of stacking under it, which pushed the
 * circle above the row's centre line.
 */
.tx-quick-board__undo {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  background: transparent;
  color: #acbff2;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 32px;
    height: 32px;
    display: block;
  }

  small {
    position: absolute;
    left: 0;
    bottom: -6px;
    width: 32px;
    height: 11px;
    border-radius: 3px;
    background: #324a8d;
    font-size: 8px;
    font-weight: 700;
    line-height: 11px;
    text-align: center;
    text-transform: uppercase;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.tx-quick-board__arrow {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border: 1px solid #4e6c7c !important;
  border-radius: 50%;
  background: #39505d;
  display: grid;
  place-items: center;
  box-sizing: border-box;

  img {
    width: 9px;
    height: 5px;
    display: block;
    transform: rotate(-90deg);
  }

  &--next img {
    transform: rotate(90deg);
  }

  &:disabled {
    opacity: 0.4;
  }
}

.tx-quick-board__chip {
  position: relative;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85), 0 0 6px rgba(255, 255, 255, 0.9);

  img {
    width: 100%;
    height: 100%;
    display: block;
  }

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    text-shadow: 0 0 4px rgba(0, 0, 0, 0.6);

    &.small {
      font-size: 8px;
    }
  }
}

.tx-quick-board__submit {
  flex: 0 0 84px;
  width: 84px;
  height: 40px;
  margin-left: auto !important;
  background: url('~@/assets/images/bet-btn-tx.webp') center / 100% 100% no-repeat;
  color: #6c3d0e !important;
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;

  &:disabled {
    filter: grayscale(0.6) brightness(0.7);
  }
}

/*
 * Closed round: the same board drained, matching the full board -- one rule
 * rather than a second, duller palette per zone.
 *
 * Drained cell by cell, as the full board is (bet-board.vue): a filter on the
 * sections was painted over every cell in them, so the result could not stand
 * out. Every cell drains -- the member's own bets too -- except the result,
 * which brightens when it comes in; the bet chips stay bright throughout.
 */
.tx-quick-board.betting-closed {
  .tx-quick-board__main,
  .tx-quick-board__sums,
  .tx-quick-board__singles {
    pointer-events: none;
  }

  /*
   * The parity block and the singles row paint their colour on the wrapper,
   * behind transparent cells, so a drained cell kept a bright wrapper behind
   * it -- only its label dimmed. Moved onto the cells here, each drains alone.
   */
  .tx-quick-board__odd-even {
    background: none;
  }

  .tx-quick-board__parity {
    background: linear-gradient(180deg, #24a35c, #157140);
  }

  .tx-quick-board__singles {
    background: none;
  }

  .tx-quick-board__single {
    background: linear-gradient(180deg, #22416f, #18325a);
  }

  /*
   * Under an overlay, not a filter, so the bet chip inside a cell stays bright:
   * the overlay dims the cell and sits under the chip (bet-placed-chip.vue,
   * z-index 2). The full board's dim colour (`$bet-zone-dim`, bet-board.vue).
   * Cells are already positioned.
   */
  .tx-quick-board__side,
  .tx-quick-board__parity,
  .tx-quick-board__sum,
  .tx-quick-board__single {
    &:not(.result-matched)::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: rgba(5, 22, 32, 0.62);
    }

    // A bet's ring goes too: once betting closes only its chip marks it.
    &.selected:not(.result-matched) {
      box-shadow: none;
    }
  }
}

.blocked {
  opacity: 0.45;
}
</style>
